import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from './client'

// Types

export interface CampaignListStats {
  total: number
  contacted: number
  remaining: number
}

export interface CampaignList {
  id: number
  name: string
  description: string
  file_name: string
  total_contacts: number
  created_at: number
  updated_at: number
  stats: CampaignListStats
}

export interface CampaignContact {
  id: number
  campaign_list_id: number
  phone: string
  name: string
  status: string
  disposition: string | null
  notes: string
  call_sid: string | null
  called_at: number | null
  called_by: string | null
  created_at: number
}

interface UploadPreviewResponse {
  headers: string[]
  detected_mapping: { phone: string | null; name: string | null; last_name: string | null }
  preview_rows: Record<string, string>[]
  total_rows: number
}

interface UploadResponse {
  campaign_list: CampaignList
  contacts_imported: number
  contacts_skipped: number
}

export interface DuplicateGroup {
  name: string
  phone: string
  count: number
  contacts: CampaignContact[]
}

interface DuplicatesResponse {
  groups: DuplicateGroup[]
  total_duplicates: number
  group_count: number
}

// Hooks

export function useCampaignLists() {
  return useQuery({
    queryKey: ['campaign-lists'],
    queryFn: () => apiFetch<{ campaign_lists: CampaignList[] }>('/campaigns/lists'),
    select: (data) => data.campaign_lists,
  })
}

export function useCampaignList(id: number | undefined) {
  return useQuery({
    queryKey: ['campaign-list', id],
    queryFn: () => apiFetch<{ campaign_list: CampaignList }>(`/campaigns/lists/${id}`),
    enabled: id != null,
    select: (data) => data.campaign_list,
  })
}

export function useCampaignContacts(
  listId: number | undefined,
  params?: { limit?: number; offset?: number; status?: string }
) {
  return useQuery({
    queryKey: ['campaign-contacts', listId, params],
    queryFn: () => {
      const sp = new URLSearchParams()
      if (params?.limit) sp.set('limit', String(params.limit))
      if (params?.offset) sp.set('offset', String(params.offset))
      if (params?.status) sp.set('status', params.status)
      const qs = sp.toString()
      return apiFetch<{ contacts: CampaignContact[]; total: number }>(
        `/campaigns/lists/${listId}/contacts${qs ? `?${qs}` : ''}`
      )
    },
    enabled: listId != null,
  })
}

export function useNextCampaignContacts(listId: number | undefined, count = 1) {
  return useQuery({
    queryKey: ['campaign-next', listId, count],
    queryFn: () =>
      apiFetch<{ contacts: CampaignContact[]; remaining: number }>(
        `/campaigns/lists/${listId}/next?count=${count}`
      ),
    enabled: listId != null,
  })
}

export function useUploadPreview() {
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData()
      form.append('file', file)
      form.append('preview_only', 'true')
      return fetch('/campaigns/lists/upload', { method: 'POST', body: form }).then(
        async (res) => {
          if (!res.ok) throw new Error((await res.json()).error || `Upload failed: ${res.status}`)
          return res.json() as Promise<UploadPreviewResponse>
        }
      )
    },
  })
}

export function useUploadCampaignList() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: {
      file: File
      name: string
      description?: string
      phoneColumn?: string
      nameColumn?: string
      lastNameColumn?: string
    }) => {
      const form = new FormData()
      form.append('file', params.file)
      form.append('name', params.name)
      if (params.description) form.append('description', params.description)
      if (params.phoneColumn) form.append('phone_column', params.phoneColumn)
      if (params.nameColumn) form.append('name_column', params.nameColumn)
      if (params.lastNameColumn) form.append('last_name_column', params.lastNameColumn)
      return fetch('/campaigns/lists/upload', { method: 'POST', body: form }).then(
        async (res) => {
          if (!res.ok) throw new Error((await res.json()).error || `Upload failed: ${res.status}`)
          return res.json() as Promise<UploadResponse>
        }
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-lists'] })
    },
  })
}

export function useUpdateCampaignContact() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      contactId,
      ...data
    }: {
      contactId: number
      status?: string
      disposition?: string
      notes?: string
      call_sid?: string
      called_at?: number
      called_by?: string
    }) =>
      apiFetch<{ contact: CampaignContact }>(`/campaigns/contacts/${contactId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-contacts'] })
      queryClient.invalidateQueries({ queryKey: ['campaign-next'] })
      queryClient.invalidateQueries({ queryKey: ['campaign-list'] })
      queryClient.invalidateQueries({ queryKey: ['campaign-lists'] })
    },
  })
}

export function useCampaignDuplicates(listId: number | undefined) {
  return useQuery({
    queryKey: ['campaign-duplicates', listId],
    queryFn: () => apiFetch<DuplicatesResponse>(`/campaigns/lists/${listId}/duplicates`),
    enabled: listId != null,
  })
}

export function useDeleteCampaignList() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch<{ success: boolean }>(`/campaigns/lists/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-lists'] })
    },
  })
}

export function useResolveDuplicates() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (listId: number) =>
      apiFetch<{ excluded_count: number; campaign_list: CampaignList }>(
        `/campaigns/lists/${listId}/duplicates/resolve`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-list'] })
      queryClient.invalidateQueries({ queryKey: ['campaign-lists'] })
      queryClient.invalidateQueries({ queryKey: ['campaign-contacts'] })
      queryClient.invalidateQueries({ queryKey: ['campaign-next'] })
      queryClient.invalidateQueries({ queryKey: ['campaign-duplicates'] })
    },
  })
}

export function useExcludeCampaignContacts() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (contactIds: number[]) =>
      apiFetch<{ excluded_count: number }>('/campaigns/contacts/exclude', {
        method: 'POST',
        body: JSON.stringify({ contact_ids: contactIds }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-list'] })
      queryClient.invalidateQueries({ queryKey: ['campaign-lists'] })
      queryClient.invalidateQueries({ queryKey: ['campaign-contacts'] })
      queryClient.invalidateQueries({ queryKey: ['campaign-next'] })
      queryClient.invalidateQueries({ queryKey: ['campaign-duplicates'] })
    },
  })
}
